import { useMemo, useRef } from "react";
import FlexSearch from "flexsearch";

import { useMachinesByOrgs } from "_/data/machines";
import { useMaterials } from "_/data/materials";
import { OrganizationWithRbac, useOrgs } from "_/data/orgs";
import { useProjects } from "_/data/projects";
import { useUsers } from "_/data/users";
import { type DocsIndexDoc, useDocsSearchIndex } from "_/data/docs";

import { Uuid } from "_/types";
import { indexById } from "_/utils";

export { type DocsIndexDoc };

export const useDocsIndex = () => {
  const { data: docsIndex, isFetched } = useDocsSearchIndex();
  const indexRef = useRef(
    new FlexSearch.Document<DocsIndexDoc, ["title", "breadcrumbs", "slug"]>({
      tokenize: "full",
      document: {
        id: "slug",
        index: ["title", "content"],
        store: ["title", "breadcrumbs", "slug"],
      },
    })
  );

  return useMemo(() => {
    if (!isFetched || !docsIndex) {
      return indexRef.current;
    }

    for (const key in docsIndex) {
      void indexRef.current.import(key, docsIndex[key]);
    }

    console.debug("Docs search index computed.");

    return indexRef.current;
  }, [docsIndex, isFetched]);
};

export type MachinesIndexDoc = {
  id: Uuid;
  name: string;
  lastSeen: string | null;
  hardwareAttached: boolean;
  hardware: {
    serial: string;
  };
  orgId: Uuid;
  orgName: string;
  inCurrentOrg: boolean;
};

export const useMachinesIndex = (
  currentOrg: OrganizationWithRbac | null | undefined
) => {
  const orgs = useOrgs().data;
  const machinesAndOrgs = useMachinesByOrgs(orgs).data;

  return useMemo(() => {
    const index = new FlexSearch.Document<MachinesIndexDoc, string[]>({
      tokenize: "full",
      document: {
        id: "id",
        index: ["name", "hardware:serial", "orgName"],
        store: [
          "id",
          "name",
          "lastSeen",
          "hardware:serial",
          "orgId",
          "orgName",
          "isInCurrentOrg",
        ],
      },
    });

    if (machinesAndOrgs) {
      // Limit indexing to machines attached to hardware; ignore detached machines.
      machinesAndOrgs
        .filter((mo) => mo.machine.hardwareAttached)
        .forEach((machineAndOrg) => {
          index.add({
            ...machineAndOrg.machine,
            orgName: machineAndOrg.org.name,
            inCurrentOrg: machineAndOrg.machine.orgId === currentOrg?.id,
          });
        });
    }

    console.debug("Machines search index computed.");

    return index;
  }, [machinesAndOrgs, currentOrg]);
};

export type ProjectsIndexDoc = {
  id: Uuid;
  name: string;
  owner: {
    id: string;
    name: string;
  };
  description: string;
  materials: Array<{
    id: string;
    name: string;
  }>;
  revisionCount: number;
};

export const useProjectsIndex = () => {
  const { data: projects = [], isFetched: projectsFetched } = useProjects();
  const { data: materials = [], isFetched: materialsFetched } = useMaterials();
  const { data: users = [], isFetched: usersFetched } = useUsers();

  // All three catalogues are required for building the projects index, so we
  // can coalesce to a single value for concision and use in the useMemo hook.
  const isFetched = projectsFetched && materialsFetched && usersFetched;

  return useMemo(() => {
    const index = new FlexSearch.Document<ProjectsIndexDoc, string[]>({
      tokenize: "full",
      document: {
        id: "id",
        index: ["name", "owner:name", "description", "materials[]:name"],
        store: [
          "id",
          "name",
          "owner",
          "description",
          "materials",
          "revisionCount",
        ],
      },
    });

    if (!isFetched) {
      return index;
    }

    const materialsById = indexById(materials);
    const usersById = indexById(users);

    projects.forEach((project) => {
      const materialsInner = project.revisions
        .flatMap((rev) => rev.materials)
        .filter(Boolean)
        .map((id) => {
          const mat = materialsById[id];
          return {
            id: mat.id,
            name: mat.name,
          };
        });

      const owner = {
        id: project.ownerId,
        name: usersById[project.ownerId].name,
      };

      index.add({
        id: project.id,
        name: project.name,
        description: project.description,
        owner,
        materials: materialsInner,
        revisionCount: project.revisions.length,
      });
    });

    console.debug("Projects search index computed.");

    return index;
  }, [projects, materials, users, isFetched]);
};
